import { useMutation } from "@tanstack/react-query";

import { hasValue, isNullish } from "@core/foundation/types";

import type { BarcodeProduct } from "@apis/calorie-grid/models";

import { barcodeProductRepository } from "../../../repository/BarcodeProductRepository.ts";
import { findGtin, isStoreInternalGtin } from "../lib/gtin.ts";
import { openFoodFactsClient } from "../lib/OpenFoodFactsClient.ts";

type BarcodeLookupResult =
  | {
      readonly barcode: string;
      readonly name: string;
      readonly status: "found";
      readonly totalCalories: number;
      readonly weightGrams: null | number;
    }
  | { readonly barcode: string; readonly status: "unknown" }
  | { readonly scanned: string; readonly status: "invalid" };

// oxlint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useBarcodeLookupActions() {
  const lookupBarcode = useMutation({
    mutationFn: async (scanned: string): Promise<BarcodeLookupResult> => {
      const barcode = findGtin(scanned);

      if (isNullish(barcode)) {
        return { scanned, status: "invalid" };
      }

      if (isStoreInternalGtin(barcode)) {
        return { barcode, status: "unknown" };
      }

      const product = await findProduct(barcode);

      if (
        isNullish(product) ||
        isNullish(product.name) ||
        isNullish(product.totalCalories) ||
        product.totalCalories <= 0
      ) {
        return { barcode, status: "unknown" };
      }

      return {
        barcode,
        name: product.name,
        status: "found",
        totalCalories: product.totalCalories,
        weightGrams: product.weightGrams,
      };
    },
  });

  return { lookupBarcode };
}

async function findProduct(barcode: string): Promise<BarcodeProduct | null> {
  const cached = await barcodeProductRepository.findById({ barcode });

  if (hasValue(cached) && hasValue(cached.source)) {
    return cached;
  }

  const product = await openFoodFactsClient.findByBarcode(barcode);

  if (isNullish(product)) {
    return null;
  }

  return barcodeProductRepository.upsert(product, () => {
    return {
      brand: product.brand,
      name: product.name,
      raw: product.raw,
      source: product.source,
      totalCalories: product.totalCalories,
      weightGrams: product.weightGrams,
    };
  });
}
